import React, { FC, useState, useCallback, useEffect, useRef } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Checkbox,
  MenuItem,
  Select,
  TextField,
  Typography,
  CircularProgress,
  Divider,
} from '@material-ui/core';
import { useApolloClient } from '@apollo/client';
import { useToaster } from '@/components/core/Toaster';
import {
  REFRESH_EXECUTIVE_KPI_CACHE,
  INVOKE_ROLLUP_COORDINATOR,
  INVOKE_ROLLUP_BACKFILL,
  BACKFILL_RUN_STATUS,
  EXECUTIVE_PERFORMANCE_SUMMARY_QUERY,
  InvokeRollupCoordinatorData,
  InvokeRollupCoordinatorVars,
  InvokeRollupBackfillData,
  InvokeRollupBackfillVars,
  BackfillRunStatusData,
  BackfillRunStatusVars,
  BackfillRunStatus,
} from '@/graph/group-performance';

interface ExecutiveAdminDialogProps {
  open: boolean;
  onClose: () => void;
}

const ExecutiveAdminDialog: FC<ExecutiveAdminDialogProps> = ({ open, onClose }) => {
  const client = useApolloClient();
  const toaster = useToaster();

  // Cache refresh state
  const [refreshingCache, setRefreshingCache] = useState(false);

  // Coordinator state
  const [invokingCoordinator, setInvokingCoordinator] = useState(false);
  const [coordinatorDryRun, setCoordinatorDryRun] = useState(false);
  const [coordinatorRunId, setCoordinatorRunId] = useState<string | null>(null);
  const [coordinatorStatus, setCoordinatorStatus] = useState<BackfillRunStatus | null>(null);

  // Backfill state
  const [invokingBackfill, setInvokingBackfill] = useState(false);
  const [backfillDays, setBackfillDays] = useState(30);
  const [backfillMode, setBackfillMode] = useState<'missing_only' | 'overwrite_all'>('missing_only');
  const [backfillDryRun, setBackfillDryRun] = useState(false);
  const [backfillRunId, setBackfillRunId] = useState<string | null>(null);
  const [backfillStatus, setBackfillStatus] = useState<BackfillRunStatus | null>(null);

  // Shared polling refs (one per section)
  const coordinatorPollRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const backfillPollRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleRefreshCache = useCallback(async () => {
    setRefreshingCache(true);
    try {
      await client.mutate({ mutation: REFRESH_EXECUTIVE_KPI_CACHE });
      await client.refetchQueries({ include: [EXECUTIVE_PERFORMANCE_SUMMARY_QUERY] });
      toaster.success('Executive KPI cache refreshed');
    } catch (err: any) {
      toaster.error(`Cache refresh failed: ${err.message}`);
    } finally {
      setRefreshingCache(false);
    }
  }, [client, toaster]);

  // Clean up polling on unmount
  useEffect(
    () => () => {
      if (coordinatorPollRef.current) clearTimeout(coordinatorPollRef.current);
      if (backfillPollRef.current) clearTimeout(backfillPollRef.current);
    },
    []
  );

  const stopPoll = useCallback((ref: React.MutableRefObject<ReturnType<typeof setTimeout> | null>) => {
    if (ref.current) {
      clearTimeout(ref.current);
      ref.current = null;
    }
  }, []);

  const startRunStatusPolling = useCallback(
    (
      runId: string,
      label: string,
      pollRef: React.MutableRefObject<ReturnType<typeof setTimeout> | null>,
      setStatus: (s: BackfillRunStatus | null) => void,
      setInvoking: (v: boolean) => void
    ) => {
      stopPoll(pollRef);
      let attempts = 0;
      let nullCount = 0;
      const maxAttempts = 60; // 3 minutes at 3s intervals
      const maxNullAttempts = 5; // give up if no run record after ~15s

      const poll = async () => {
        attempts++;
        try {
          const { data } = await client.query<BackfillRunStatusData, BackfillRunStatusVars>({
            query: BACKFILL_RUN_STATUS,
            variables: { runId },
            fetchPolicy: 'network-only',
          });
          const status = data?.backfillRunStatus;
          if (status) {
            setStatus(status);
            // Done when coordinator/backfill itself finished AND all worker jobs completed
            const allJobsDone =
              status.enqueuedJobs > 0 ? status.completedJobs >= status.enqueuedJobs : status.status !== 'running';
            if (status.status !== 'running' && allJobsDone) {
              pollRef.current = null;
              setInvoking(false);
              const failed = status.enqueueFailures > 0 || status.status !== 'success';
              if (failed) {
                toaster.error(
                  `${label} ${status.status}: ${status.completedJobs}/${status.enqueuedJobs} jobs, ${status.enqueueFailures} failures`
                );
              } else {
                toaster.success(`${label} complete: ${status.completedJobs}/${status.enqueuedJobs} jobs`);
              }
              return;
            } else if (attempts >= maxAttempts) {
              pollRef.current = null;
              setInvoking(false);
              toaster.error(
                `${label} polling timed out: status=${status.status}, ${status.completedJobs}/${status.enqueuedJobs} jobs`
              );
              return;
            }
          } else {
            nullCount++;
            if (nullCount >= maxNullAttempts) {
              pollRef.current = null;
              setInvoking(false);
              toaster.error(
                `${label} failed: no run record found. The Lambda may have crashed before starting — check CloudWatch logs.`
              );
              return;
            }
          }
        } catch (err) {
          console.error(`${label} status poll error:`, err);
          if (attempts >= maxAttempts) {
            pollRef.current = null;
            setInvoking(false);
            toaster.error(`${label} status polling failed`);
            return;
          }
        }
        // Schedule next poll only after current one completes
        pollRef.current = setTimeout(poll, 3000);
      };

      poll();
    },
    [client, toaster, stopPoll]
  );

  const handleInvokeCoordinator = useCallback(async () => {
    setInvokingCoordinator(true);
    setCoordinatorStatus(null);
    setCoordinatorRunId(null);
    try {
      const { data } = await client.mutate<InvokeRollupCoordinatorData, InvokeRollupCoordinatorVars>({
        mutation: INVOKE_ROLLUP_COORDINATOR,
        variables: { dryRun: coordinatorDryRun },
      });
      const result = data?.invokeRollupCoordinator;
      const needsPolling = result?.message?.includes('Poll backfillRunStatus');
      if (result?.success) {
        if (needsPolling && result.runId) {
          setCoordinatorRunId(result.runId);
          toaster.success('Coordinator started');
          startRunStatusPolling(
            result.runId,
            'Coordinator',
            coordinatorPollRef,
            setCoordinatorStatus,
            setInvokingCoordinator
          );
        } else {
          toaster.success(`Coordinator invoked${coordinatorDryRun ? ' (dry run)' : ''}`);
          setInvokingCoordinator(false);
        }
      } else {
        toaster.error(`Coordinator failed: ${result?.message ?? 'Unknown error'}`);
        setInvokingCoordinator(false);
      }
    } catch (err: any) {
      toaster.error(`Coordinator invocation failed: ${err.message}`);
      setInvokingCoordinator(false);
    }
  }, [client, toaster, coordinatorDryRun, startRunStatusPolling]);

  const handleInvokeBackfill = useCallback(async () => {
    setInvokingBackfill(true);
    setBackfillStatus(null);
    setBackfillRunId(null);
    try {
      const { data } = await client.mutate<InvokeRollupBackfillData, InvokeRollupBackfillVars>({
        mutation: INVOKE_ROLLUP_BACKFILL,
        variables: { backfillDays, backfillMode, dryRun: backfillDryRun },
      });
      const result = data?.invokeRollupBackfill;
      const needsPolling = result?.message?.includes('Poll backfillRunStatus');
      if (result?.success) {
        if (needsPolling && result.runId) {
          setBackfillRunId(result.runId);
          toaster.success('Backfill started');
          startRunStatusPolling(result.runId, 'Backfill', backfillPollRef, setBackfillStatus, setInvokingBackfill);
        } else {
          toaster.success('Backfill complete');
          setInvokingBackfill(false);
        }
      } else {
        toaster.error(`Backfill failed: ${result?.message ?? 'Unknown error'}`);
        setInvokingBackfill(false);
      }
    } catch (err: any) {
      toaster.error(`Backfill invocation failed: ${err.message}`);
      setInvokingBackfill(false);
    }
  }, [client, toaster, backfillDays, backfillMode, backfillDryRun, startRunStatusPolling]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Administration Options</DialogTitle>
      <DialogContent>
        {/* Cache Refresh */}
        <Box py={1}>
          <Typography variant="subtitle2">Cache Management</Typography>
          <Typography variant="body2" color="textSecondary" gutterBottom>
            Clear the executive KPI rollup cache.
          </Typography>
          <Button
            variant="outlined"
            size="small"
            color="primary"
            onClick={handleRefreshCache}
            disabled={refreshingCache}
          >
            {refreshingCache ? <CircularProgress size={16} style={{ marginRight: 8 }} /> : null}
            Refresh Cache
          </Button>
        </Box>

        <Divider />

        {/* Coordinator */}
        <Box py={1}>
          <Typography variant="subtitle2">Rollup Coordinator</Typography>
          <Typography variant="body2" color="textSecondary" gutterBottom>
            Invoke Coordinator function that calculates daily executive KPI rollup for past.
          </Typography>
          <Box display="flex" alignItems="center" style={{ gap: 16 }} mb={1.5} flexWrap="wrap">
            <FormControlLabel
              control={
                <Checkbox
                  checked={coordinatorDryRun}
                  onChange={e => setCoordinatorDryRun(e.target.checked)}
                  size="small"
                />
              }
              label={<Typography variant="body2">Dry run</Typography>}
            />
          </Box>
          <Button
            variant="outlined"
            size="small"
            color="primary"
            onClick={handleInvokeCoordinator}
            disabled={invokingCoordinator}
          >
            {invokingCoordinator ? <CircularProgress size={16} style={{ marginRight: 8 }} /> : null}
            Invoke Coordinator
          </Button>
          {coordinatorStatus && coordinatorRunId && (
            <Box mt={1}>
              <Typography variant="body2" color="textSecondary">
                Jobs: {coordinatorStatus.completedJobs}/{coordinatorStatus.enqueuedJobs}
                {coordinatorStatus.completedJobs < coordinatorStatus.enqueuedJobs ? ' ...' : ' done'}
                {coordinatorStatus.enqueueFailures > 0 && ` | Failures: ${coordinatorStatus.enqueueFailures}`}
                {coordinatorStatus.durationMs != null && ` | ${(coordinatorStatus.durationMs / 1000).toFixed(1)}s`}
              </Typography>
            </Box>
          )}
        </Box>

        <Divider />

        {/* Backfill */}
        <Box py={1}>
          <Typography variant="subtitle2">Rollup Backfill</Typography>
          <Typography variant="body2" color="textSecondary" gutterBottom>
            Invoke the backfill functions to recompute daily executive KPI rollups for a configurable number of past
            days.
          </Typography>
          <Box display="flex" alignItems="center" style={{ gap: 16 }} mb={1.5} flexWrap="wrap">
            <FormControl size="small" style={{ minWidth: 100 }}>
              <TextField
                label="Days"
                type="number"
                size="small"
                variant="outlined"
                value={backfillDays}
                onChange={e => setBackfillDays(Math.max(1, Math.min(365, parseInt(e.target.value) || 1)))}
                inputProps={{ min: 1, max: 365 }}
                style={{ width: 90 }}
              />
            </FormControl>
            <FormControl size="small" variant="outlined" style={{ minWidth: 100 }}>
              <Select
                value={backfillMode}
                onChange={e => setBackfillMode(e.target.value as 'missing_only' | 'overwrite_all')}
              >
                <MenuItem value="missing_only">Missing only</MenuItem>
                <MenuItem value="overwrite_all">Overwrite all</MenuItem>
              </Select>
            </FormControl>
            <FormControlLabel
              control={
                <Checkbox checked={backfillDryRun} onChange={e => setBackfillDryRun(e.target.checked)} size="small" />
              }
              label={<Typography variant="body2">Dry run</Typography>}
            />
          </Box>
          <Button
            variant="outlined"
            size="small"
            color="primary"
            onClick={handleInvokeBackfill}
            disabled={invokingBackfill}
          >
            {invokingBackfill ? <CircularProgress size={16} style={{ marginRight: 8 }} /> : null}
            Invoke Backfill
          </Button>
          {backfillStatus && backfillRunId && (
            <Box mt={1}>
              <Typography variant="body2" color="textSecondary">
                Jobs: {backfillStatus.completedJobs}/{backfillStatus.enqueuedJobs}
                {backfillStatus.completedJobs < backfillStatus.enqueuedJobs ? ' ...' : ' done'}
                {backfillStatus.enqueueFailures > 0 && ` | Failures: ${backfillStatus.enqueueFailures}`}
                {backfillStatus.durationMs != null && ` | ${(backfillStatus.durationMs / 1000).toFixed(1)}s`}
              </Typography>
            </Box>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default ExecutiveAdminDialog;
